import React, { useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Header from '../../components/header/header';
import usePollingData from '../../hooks/usePollingData';
import './PollingData.css';

/**
 * PollingDataView Component
 * Displays polling response data for a selected store
 * Shows brand preferences with percentages and progress bars
 */
const PollingDataView = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationSiteName = location.state?.siteName;

  const {
    polls,
    currentPoll,
    isLoading,
    error,
    selectedPollIndex,
    selectPoll,
    hasMultiplePolls,
    storeName
  } = usePollingData(storeId);

  // Use store name from API response, fallback to navigation state, then formatted storeId
  const displaySiteName = storeName || navigationSiteName;

  const [showPollSelector, setShowPollSelector] = useState(false);
  const cardRef = useRef(null);
  const [positionChangeAnimations, setPositionChangeAnimations] = useState({});
  const previousPositionsRef = useRef({});

  // Track position changes and apply animations when options reorder
  React.useEffect(() => {
    if (!currentPoll || !currentPoll.options || currentPoll.options.length === 0) {
      return;
    }

    // Create a sorted copy to track current positions
    const sortedOptions = [...currentPoll.options].sort((a, b) =>
      parseFloat(b.percentage) - parseFloat(a.percentage)
    );

    const newAnimations = {};
    const previousPositions = previousPositionsRef.current;

    sortedOptions.forEach((option, currentIndex) => {
      const previousIndex = previousPositions[option.option_id];

      // If we have previous position data and the position changed
      if (previousIndex !== undefined && previousIndex !== currentIndex) {
        // Determine if option moved up (became more popular) or down
        const movedUp = currentIndex < previousIndex;
        newAnimations[option.option_id] = movedUp ? 'moveUpHighlight' : 'moveDownSubtle';

        // Remove animation class after it completes (1.8 seconds for moveUp, 1.5 for moveDown)
        const animationDuration = movedUp ? 1800 : 1500;
        setTimeout(() => {
          setPositionChangeAnimations(prev => {
            const updated = { ...prev };
            delete updated[option.option_id];
            return updated;
          });
        }, animationDuration);
      }

      // Update position for next comparison
      previousPositions[option.option_id] = currentIndex;
    });

    if (Object.keys(newAnimations).length > 0) {
      setPositionChangeAnimations(newAnimations);
    }

    previousPositionsRef.current = previousPositions;
  }, [currentPoll?.options]);



  // Color palette for progress bars
  const progressBarColors = [
    '#3b82f6', // blue
    '#ef4444', // red
    '#60a5fa', // light blue
    '#10b981', // green
    '#8b5cf6', // purple
    '#f97316'  // orange
  ];

  // Get color for a specific option
  const getProgressBarColor = (index) => {
    return progressBarColors[index % progressBarColors.length];
  };

  // Format site name from store ID (replace hyphens/underscores with spaces)
  const formatSiteName = (storeId) => {
    return storeId
      .replace(/[-_]/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Get winners (options with highest percentage)
  const getWinners = () => {
    if (!currentPoll || !currentPoll.options || currentPoll.options.length === 0) {
      return [];
    }
    const maxPercentage = Math.max(...currentPoll.options.map(opt => parseFloat(opt.percentage)));
    return currentPoll.options.filter(opt => parseFloat(opt.percentage) === maxPercentage);
  };

  const winnerIds = getWinners().map(w => w.option_id);


  // Get poll title only
  const getPollDisplayTitle = () => {
    if (!currentPoll) return '';
    return currentPoll.title;
  };

  if (isLoading && !currentPoll) {
    return (
      <>
        <Header />
        <div className="polling-data-view">
          <div className="polling-loading">
            <div className="loading-spinner"></div>
            <p>Loading polling data...</p>
          </div>
        </div>
      </>
    );
  }

  if (error || !currentPoll) {
    return (
      <>
        <Header />
        <div className="polling-data-view">
          <div className="polling-container">
            <div className="polling-error">
              <p>{error || 'No polling data available for this site'}</p>
              <button
                onClick={() => navigate(`/Site/${storeId}`)}
                className="error-back-btn"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Data View
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      {/* Back to Data View Button - Fixed position */}
      <button
        onClick={() => navigate(`/Site/${storeId}`)}
        className="back-to-dataview-btn"
        title="Go back to Data View"
      >
        <ArrowLeft className="w-4 h-4" />
      </button>
      <div className="polling-data-view">
        <div className="polling-container">
          {/* Poll Selector (if multiple polls exist) */}
          {hasMultiplePolls && (
            <div className="poll-selector-wrapper">
              <button
                className="poll-selector-button"
                onClick={() => setShowPollSelector(!showPollSelector)}
              >
                <span className="selector-label">
                  {selectedPollIndex === 0
                    ? 'Current Poll'
                    : `Historical Poll ${selectedPollIndex}`}
                </span>
                <span className="selector-arrow">▼</span>
              </button>

              {showPollSelector && (
                <div className="poll-selector-dropdown">
                  {polls.map((poll, index) => (
                    <button
                      key={poll.poll_id}
                      className={`poll-option ${index === selectedPollIndex ? 'active' : ''}`}
                      onClick={() => {
                        selectPoll(index);
                        setShowPollSelector(false);
                      }}
                    >
                      <span className="poll-option-title">{poll.title}</span>
                      <span className="poll-option-date">
                        {new Date(poll.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Main Polling Results Card */}
          <div className="polling-card" ref={cardRef}>
            {/* Header with site/date/device info */}
            <div className="polling-header">
              <div className="header-meta">
                <span className="site-name">{displaySiteName || formatSiteName(storeId)}</span>
                <span className="meta-divider">•</span>
                <span className="poll-date">
                  {polls && polls.length > 0 && polls[0]?.latest_response_time
                    ? new Date(polls[0].latest_response_time).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })
                    : 'N/A'}
                </span>
                {currentPoll?.device_name && (
                  <>
                    <span className="meta-divider">•</span>
                    <span className="device-name">{currentPoll.device_name}</span>
                  </>
                )}
              </div>
              <h1 className="polling-title">{getPollDisplayTitle()}</h1>
            </div>

            {/* Results */}
            <div className="polling-results">
              {currentPoll.options && currentPoll.options.length > 0 ? (
                [...currentPoll.options].sort((a, b) => parseFloat(b.percentage) - parseFloat(a.percentage)).map((option, index) => {
                  const animationClass = positionChangeAnimations[option.option_id];
                  const animationDuration = animationClass === 'moveUpHighlight' ? '1.8s' : '1.5s';
                  return (
                  <div
                    key={option.option_id}
                    className="polling-option-card"
                    style={animationClass ? { animation: `${animationClass} ${animationDuration} cubic-bezier(0.34, 1.56, 0.64, 1)` } : {}}
                  >
                    <div className="polling-option">
                    {/* Icon and Label */}
                    <div className="option-icon-label">
                      {option.icon_url && (
                        <img
                          src={option.icon_url}
                          alt={option.label}
                          className="option-icon"
                        />
                      )}
                      <span
                        className="option-label polling-title"
                      >
                        {option.label}
                      </span>
                    </div>

                    {/* Metrics Column */}
                    <div className="option-metrics">
                      {/* Top Row: Vote Count (left) and Winner Badge (right) */}
                      <div className="metrics-top-row">
                        <span className="vote-count">{option.response_count} {option.response_count === 1 ? 'vote' : 'votes'}</span>
                        {winnerIds.includes(option.option_id) && (
                          <div className="winner-badge">
                            <span className="winner-symbol">★</span>
                            <span className="winner-text">Winning</span>
                          </div>
                        )}
                      </div>

                      {/* Progress Bar */}
                      <div className="progress-bar-container">
                        <div
                          className="progress-bar"
                          style={{
                            width: `${Math.round(option.percentage)}%`,
                            backgroundColor: getProgressBarColor(index)
                          }}
                        ></div>
                      </div>

                      {/* Percentage Below Progress Bar */}
                      <span className="option-percentage">{Math.round(option.percentage)}%</span>
                    </div>
                    </div>
                  </div>
                  );
                })
              ) : (
                <div className="no-options">
                  <p>No options available for this poll</p>
                </div>
              )}
            </div>

            {/* Total Votes Footer - Bottom of Card */}
            {currentPoll.total_responses > 0 && (
              <div className="polling-footer polling-title">
                <span className="footer-label">Total Votes</span>
                <span className="footer-value">{currentPoll.total_responses}</span>
              </div>
            )}

          </div>
        </div>
      </div>
    </>
  );
};

export default PollingDataView;
